import posthog from 'posthog-js';

export class PostHogService {
    private static initialized = false;

    static initialize(): void {
        if (this.initialized) return;

        const key = import.meta.env.VITE_POSTHOG_KEY;
        const host = import.meta.env.VITE_POSTHOG_HOST;

        if (!key || !host) return;

        posthog.init(key, {
            api_host: host,
            person_profiles: 'identified_only',
            persistence: 'memory',
            opt_out_capturing_by_default: true,
            capture_pageview: false,
            capture_pageleave: true,
        });

        this.initialized = true;
    }

    static optIn(): void {
        if (!this.initialized) return;
        posthog.opt_in_capturing();
        posthog.set_config({ persistence: 'localStorage+cookie' });
        posthog.capture('$pageview');
    }

    static identify(distinctId: string, properties?: Record<string, unknown>): void {
        if (!this.initialized) return;
        posthog.identify(distinctId, properties);
    }

    static capture(event: string, properties?: Record<string, unknown>): void {
        if (!this.initialized) return;
        posthog.capture(event, properties);
    }

    static reset(): void {
        if (!this.initialized) return;
        posthog.reset();
    }

    static getDistinctId(): string | null {
        if (!this.initialized) return null;
        return posthog.get_distinct_id() ?? null;
    }

    static isFeatureEnabled(flag: string): boolean | undefined {
        if (!this.initialized || !this.isOptedIn()) return undefined;
        return posthog.isFeatureEnabled(flag);
    }

    static onFeatureFlagsLoaded(callback: () => void): void {
        if (!this.initialized || !this.isOptedIn()) return;
        posthog.onFeatureFlags(callback);
    }

    static isOptedIn(): boolean {
        if (!this.initialized) return false;
        return posthog.has_opted_in_capturing();
    }

    static disable(): void {
        if (!this.initialized) return;
        posthog.opt_out_capturing();
        posthog.set_config({ persistence: 'memory' });
        this.clearPostHogStorage();
    }

    private static clearPostHogStorage(): void {
        const key = import.meta.env.VITE_POSTHOG_KEY;

        // Clear PostHog cookies (ph_* prefixed)
        document.cookie.split(';').forEach(cookie => {
            const name = cookie.split('=')[0].trim();
            if (name.startsWith('ph_')) 
                document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
            
        });

        // Clear PostHog localStorage entries
        if (key) {
            try {
                Object.keys(localStorage).forEach(storageKey => {
                    if (storageKey.startsWith(`ph_${key}`)) 
                        localStorage.removeItem(storageKey);
                    
                });
            } catch {
                // localStorage may not be available
            }
        }
    }
}
